import { AnimatePresence, motion } from 'framer-motion'
import { useSimStore } from '../store/simStore'

export function InspectorPanel() {
  const { inspector, setInspector, instanceConfig, machines, activeJobs } = useSimStore()

  const close = () => setInspector(null)

  let content: React.ReactNode = null

  if (inspector?.kind === 'machine') {
    const id = inspector.id
    const cfg = instanceConfig?.machines.find((m) => m.id === id)
    const state = machines[id]
    const queued = activeJobs.filter((j) => j.machine_id === id)
    content = (
      <div className="space-y-3">
        <div className="text-teal-400 font-bold text-base">{id}</div>
        {cfg && (
          <div className="space-y-1 text-xs">
            {[
              ['Schedule',   cfg.work_schedule],
              ['Skills',     cfg.skills.join(', ') || '—'],
              ['Capacity',   cfg.capacity],
              ...(cfg.mtbf > 0 ? [['MTBF', `${cfg.mtbf} min`], ['MTTR', `${cfg.mttr} min`]] : []),
            ].map(([k, v]) => (
              <div key={String(k)} className="flex justify-between">
                <span className="text-slate-400">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        )}
        {state && (
          <div className="space-y-1 text-xs">
            <div className="text-slate-400 uppercase tracking-wider text-[10px] mt-2 mb-1">Live State</div>
            {[
              ['Status',       state.state],
              ['On-shift',     state.on_shift ? 'Yes' : 'Off'],
              ['Utilisation',  `${state.utilisation_pct.toFixed(1)}%`],
              ['Queue depth',  state.queue_depth],
              ['Current order', state.current_order],
            ].map(([k, v]) => (
              <div key={String(k)} className="flex justify-between">
                <span className="text-slate-400">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        )}
        {queued.length > 0 && (
          <div>
            <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">In Service</div>
            {queued.map((j) => (
              <div key={j.order_id} className="text-xs font-mono text-teal-400">
                {j.order_id.slice(-8)}{j.is_late ? ' ⚠' : ''}
                {j.actual_start > 0 && (
                  <span className="text-slate-500 ml-1">@{j.actual_start.toFixed(0)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (inspector?.kind === 'job') {
    const id = inspector.id
    const job = activeJobs.find((j) => j.order_id === id)
    const order = instanceConfig?.orders.find((o) => o.order_id === id)
    content = (
      <div className="space-y-3">
        <div className="text-teal-400 font-bold">{id}</div>
        {order && (
          <div className="space-y-1 text-xs">
            {[
              ['Material',   order.fg_material],
              ['Quantity',   order.quantity],
              ['Priority',   order.priority],
              ['Release',    `${order.release_date} min`],
              ['Due',        `${order.due_date} min`],
              ['Status',     order.status],
            ].map(([k, v]) => (
              <div key={String(k)} className="flex justify-between">
                <span className="text-slate-400">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        )}
        {job && (
          <div className="text-xs space-y-1">
            <div className="text-slate-400 uppercase tracking-wider text-[10px] mt-2 mb-1">Routing</div>
            {order?.routing.map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full inline-block ${i < job.routing_row ? 'bg-emerald-400' : i === job.routing_row ? 'bg-teal-400 animate-pulse' : 'bg-[#334155]'}`} />
                <span className="font-mono text-slate-300">{step.machine_skill}</span>
                <span className="text-slate-500">{step.processing_time.toFixed(0)}m</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <AnimatePresence>
      {inspector && (
        <motion.div
          key="inspector"
          initial={{ x: 320, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 320, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 25 }}
          className="absolute top-14 right-64 bottom-36 w-56 bg-[#161b22]/95 border-l border-[#30363d] p-4 z-30 overflow-y-auto"
        >
          <button
            onClick={close}
            className="absolute top-2 right-2 text-slate-500 hover:text-white text-lg"
          >
            ×
          </button>
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
